import { BuildingOffice2Icon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { DESK_CATEGORY_OPTIONS } from "../newsquawkCategories";

/**
 * Worked examples for the two desk notes.
 *
 * Both fields are blank boxes asking for prose, which is the hardest thing to
 * put in front of someone mid-task — "phrases a trader might say" in the
 * abstract is a puzzle, whereas a filled-in Fed example is a template you edit.
 * One click fills both, so the desk starts from something shaped correctly and
 * changes the words.
 */
const EXAMPLE = {
  description: "Voting and non-voting FOMC members by surname, plus the Chair.",
  whenToUse: "rate cuts; hikes; policy path; dot plot; FOMC",
};

const DESCRIPTION_MAX = 500;
const WHEN_TO_USE_MAX = 2000;

const FIELD =
  "mt-1 w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 " +
  "placeholder:text-gray-500 focus:border-squawk-blue focus:outline-none";

const LABEL = "text-xs font-medium uppercase tracking-wide text-gray-400";

interface DeskPublishFieldsProps {
  publish: boolean;
  onPublishChange: (next: boolean) => void;
  description: string;
  onDescriptionChange: (next: string) => void;
  whenToUse: string;
  onWhenToUseChange: (next: string) => void;
  categories: string[];
  onCategoriesChange: (next: string[]) => void;
  /**
   * Already in the Newsquawk library. The toggle becomes a static statement:
   * the server ignores `owner_type` on PATCH, so ownership cannot change after
   * creation and offering a switch would be a lie.
   */
  alreadyPublished?: boolean;
}

/**
 * Desk-only controls for publishing to the Newsquawk library.
 *
 * Rendered only behind `useIsDeskUser()`. Nothing here is a security boundary —
 * pref-storage enforces the same rule and 403s a forged request — it exists so
 * the desk can do in the portal what used to be a database job.
 */
export default function DeskPublishFields({
  publish,
  onPublishChange,
  description,
  onDescriptionChange,
  whenToUse,
  onWhenToUseChange,
  categories,
  onCategoriesChange,
  alreadyPublished = false,
}: DeskPublishFieldsProps) {
  /**
   * "All" is exclusive — a list is either cross-asset or it is a specific set,
   * and "all + energy" says nothing coherent.
   */
  const toggleCategory = (id: string) => {
    if (id === "all") {
      onCategoriesChange(categories.includes("all") ? [] : ["all"]);
      return;
    }
    const without = categories.filter((c) => c !== "all");
    onCategoriesChange(
      without.includes(id)
        ? without.filter((c) => c !== id)
        : [...without, id]
    );
  };
  const showNotes = publish || alreadyPublished;

  const fillExample = () => {
    onDescriptionChange(EXAMPLE.description);
    onWhenToUseChange(EXAMPLE.whenToUse);
  };

  return (
    <div className="rounded border border-squawk-blue/30 bg-squawk-blue/[0.04] p-3">
      <div className="flex items-start gap-2.5">
        <BuildingOffice2Icon className="mt-0.5 size-4 shrink-0 text-squawk-blue" />
        <div className="min-w-0 flex-1">
          {alreadyPublished ? (
            <p className="text-sm font-medium text-gray-100">
              In the Newsquawk library
              <span className="ml-2 text-xs font-normal text-gray-400">
                — every customer can see this list
              </span>
            </p>
          ) : (
            <label className="flex cursor-pointer items-start gap-2.5">
              <input
                type="checkbox"
                checked={publish}
                onChange={(e) => onPublishChange(e.target.checked)}
                className="mt-0.5 size-4 shrink-0 cursor-pointer accent-squawk-blue"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-100">
                  Publish to the Newsquawk library
                </span>
                <span className="mt-0.5 block text-xs leading-relaxed text-gray-400">
                  Saves as a Newsquawk list rather than your own. Visible to
                  every customer as soon as you save — there is no draft state,
                  and ownership can&apos;t be changed afterwards.
                </span>
              </span>
            </label>
          )}
        </div>
      </div>

      {showNotes && (
        <div className="mt-3.5 space-y-3 border-t border-squawk-blue/20 pt-3.5">
          <div className="flex items-center justify-between gap-3">
            <span className={LABEL}>Notes</span>
            <button
              type="button"
              onClick={fillExample}
              className="cursor-pointer border-none bg-transparent p-0 text-xs text-squawk-blue transition-colors hover:text-squawk-blue-light"
            >
              Fill an example
            </button>
          </div>

          <div>
            <span className={LABEL}>Asset classes</span>
            <p className="mt-0.5 text-xs text-gray-500">
              Pick every asset class this applies to. Groups the library and
              matches it to what a customer picked at sign-up.
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {DESK_CATEGORY_OPTIONS.map((option) => {
                const active = categories.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => toggleCategory(option.id)}
                    aria-pressed={active}
                    className={clsx(
                      "cursor-pointer rounded border px-2 py-1 text-xs transition-colors",
                      active
                        ? "border-squawk-blue bg-squawk-blue/15 text-squawk-blue"
                        : "border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600 hover:text-gray-200"
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {categories.length === 0 && (
              <p className="mt-1 text-xs text-amber-400">
                Without this it groups under &ldquo;Other&rdquo;.
              </p>
            )}
          </div>

          <div>
            <label className={LABEL} htmlFor="nq-desk-description">
              What it covers
            </label>
            <p className="mt-0.5 text-xs text-gray-500">
              One line, shown to customers alongside the list.
            </p>
            <input
              id="nq-desk-description"
              type="text"
              value={description}
              maxLength={DESCRIPTION_MAX}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder={EXAMPLE.description}
              className={FIELD}
            />
          </div>

          <div>
            <label className={LABEL} htmlFor="nq-desk-when-to-use">
              What someone might ask for it
            </label>
            {/* The field is only hard while it is abstract. Naming the unit
                ("things a customer would type"), showing the separator and
                giving a filled example turns it into a fill-in-the-blank. */}
            <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
              Things a customer might type when they want this list, separated
              by semicolons. Used to match their words to this list — never
              shown to them.
            </p>
            <textarea
              id="nq-desk-when-to-use"
              value={whenToUse}
              maxLength={WHEN_TO_USE_MAX}
              rows={2}
              onChange={(e) => onWhenToUseChange(e.target.value)}
              placeholder={EXAMPLE.whenToUse}
              className={`${FIELD} resize-none`}
            />
          </div>
        </div>
      )}
    </div>
  );
}
